'use client'

import { useTranslation } from '@shared/config/i18n'

import { Button, Checkbox, HStack, VStack } from '@podo-app/design-system-temp'

import { NPS_LOW_RATING_THRESHOLD } from './nps-survey.constants'
import type { NpsRating } from './nps-survey.types'

interface NpsCompleteStepProps {
  rating: NpsRating
  wantBlock: boolean
  onToggleBlock: () => void
  wantFavorite: boolean
  onToggleFavorite: () => void
  onComplete: () => void
  /** Show the favorite opt-in checkbox (rating>=4 AND tutor not already blocked). */
  canFavorite?: boolean
  /** Show the block opt-in checkbox (rating<=2 AND tutor not already blocked). */
  canBlock?: boolean
}

export const NpsCompleteStep = ({
  rating,
  wantBlock,
  onToggleBlock,
  wantFavorite,
  onToggleFavorite,
  onComplete,
  canFavorite,
  canBlock,
}: NpsCompleteStepProps) => {
  const t = useTranslation()
  const isLowRating = rating <= NPS_LOW_RATING_THRESHOLD
  // When canBlock/canFavorite are explicitly provided by the flow, use them.
  // Fall back to the rating-only logic so the component stays usable standalone.
  const showBlockCheckbox = canBlock !== undefined ? canBlock : isLowRating
  const showFavoriteCheckbox = canFavorite !== undefined ? canFavorite : !isLowRating

  return (
    <VStack className="h-dvh" data-testid="nps-complete-step">
      <VStack className="flex-1 items-center justify-center gap-10 overflow-y-auto overflow-x-hidden scrollbar-hide [&::-webkit-scrollbar]:hidden p-5">
        <VStack className="items-center gap-2 text-center">
          <span className="text-[18px] font-bold leading-[26px] tracking-[-0.36px] text-[#1C1C1C]">
            {t('lessonReview.nps.complete.title')}
          </span>
          <div className="flex flex-col">
            {isLowRating ? (
              <>
                <span className="text-[14px] font-medium leading-5 text-[#757575]">
                  {t('lessonReview.nps.complete.lowRatingLine1')}
                </span>
                <span className="text-[14px] font-medium leading-5 text-[#757575]">
                  {t('lessonReview.nps.complete.lowRatingLine2')}
                </span>
              </>
            ) : (
              <>
                <span className="text-[14px] font-medium leading-5 text-[#757575]">
                  {t('lessonReview.nps.complete.highRatingLine1')}
                </span>
                <span className="text-[14px] font-medium leading-5 text-[#757575]">
                  {t('lessonReview.nps.complete.highRatingLine2')}
                </span>
              </>
            )}
          </div>
        </VStack>

        {showBlockCheckbox && (
          <button
            type="button"
            className="bg-white border border-[#e8e8e8] rounded-lg px-4 py-3 cursor-pointer whitespace-nowrap"
            onClick={onToggleBlock}
            data-testid="btn-toggle-block-tutor"
          >
            <HStack className="items-center gap-2">
              <Checkbox checked={wantBlock} />
              <span className="text-[14px] font-medium leading-5 text-[#1C1C1C]">
                {t('lessonReview.nps.complete.blockTutor')}
              </span>
            </HStack>
          </button>
        )}

        {showFavoriteCheckbox && (
          <button
            type="button"
            className="bg-white border border-[#e8e8e8] rounded-lg px-4 py-3 cursor-pointer whitespace-nowrap"
            onClick={onToggleFavorite}
            data-testid="btn-toggle-favorite-tutor"
          >
            <HStack className="items-center gap-2">
              <Checkbox checked={wantFavorite} />
              <span className="text-[14px] font-medium leading-5 text-[#1C1C1C]">
                {t('lessonReview.nps.complete.favoriteTutor')}
              </span>
            </HStack>
          </button>
        )}
      </VStack>

      <VStack className="w-full px-5 py-2">
        <Button size="xLarge" className="w-full" onClick={onComplete}>
          {t('common.actions.confirm')}
        </Button>
      </VStack>
    </VStack>
  )
}
